// 📄 organizationService.ts
import {
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { db, functionsClient } from '../firebase';
import { Organization, OrgMember, UserOrgLink, OrgType, OrgRoleAsignable, PendingInvitation } from '../types/organization';
import { queueEmail } from './mailService.ts';
import { notifySlackOrg } from './slackService.ts';

export type { Organization, OrgMember, UserOrgLink, PendingInvitation, OrgRoleAsignable };

const ROL_LABEL: Record<string, string> = { admin: 'Admin', miembro: 'Miembro' };

// Botón "Abrir OneHub" de los correos de organización: apunta al mismo sitio
// desde el que se invitó (QAS → qas.getonehub.com, PRD → getonehub.com).
const botonOneHub = (texto: string) => {
  const url = `${window.location.origin}/`;
  return `<p><a href="${url}" style="display:inline-block;background:#4f46e5;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;font-weight:600">${texto}</a></p>
      <p style="color:#888;font-size:12px">O entra en ${url}</p>`;
};

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

/**
 * Id determinístico de la organización personal de un usuario. Permite
 * que ensurePersonalOrganization sea idempotente sin necesitar una query
 * previa: dos llamadas concurrentes escriben el mismo doc, nunca crean
 * dos organizaciones personales distintas para el mismo usuario.
 */
export const personalOrgId = (uid: string): string => `personal_${uid}`;

// Todos los límites anti-abuso (equipos, proyectos, miembros por proyecto,
// miembros por organización) viven en users/{uid} desde el rediseño de
// 2026-08-19 — ver las constantes LIMITE_*_DEFAULT en userService.ts. Se
// centralizaron ahí porque un tope "por organización" se podía esquivar
// creando más organizaciones. Ver proyecto_limites_organizacion.

/**
 * Garantiza que `uid` tenga una organización personal (tipo "personal",
 * invisible en UI, 1 solo miembro = el owner). Se llama desde
 * ensureUserProfile en cada login (registro tradicional, Google,
 * Microsoft — todos pasan por ahí). Idempotente: si ya existe, no hace
 * ningún write y devuelve el mismo orgId de siempre.
 *
 * Escribe 3 documentos en un solo batch:
 *   - organizations/{orgId}                     (metadata de la org)
 *   - organizations/{orgId}/miembros/{uid}       (fuente de verdad de membresía)
 *   - usuarios/{uid}/organizaciones/{orgId}      (mirror liviano para "mis orgs")
 */
export const ensurePersonalOrganization = async (
  uid: string,
  email: string,
  displayName?: string | null
): Promise<string> => {
  if (!uid) throw new Error("UID no proporcionado.");

  const orgId = personalOrgId(uid);
  const orgRef = doc(db, "organizations", orgId);

  const existing = await getDoc(orgRef);
  if (existing.exists()) {
    return orgId;
  }

  const normalizedEmail = (email || '').toLowerCase();
  const nombre = `Personal de ${displayName?.trim() || normalizedEmail || uid}`;

  const batch = writeBatch(db);

  batch.set(orgRef, {
    name: nombre,
    type: 'personal',
    ownerId: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
    // Los límites anti-abuso viven en users/{uid}, no acá (ver
    // proyecto_limites_organizacion) — ensureUserProfile ya los siembra al
    // crear el perfil, en el mismo flujo de login que llama a esta
    // función. cantidadMiembros tampoco se setea acá — lo escribe el
    // trigger onOrgMemberCreated al crearse el doc de miembros/{uid} del
    // owner, en el mismo batch de abajo (evita doble conteo).
  });

  const memberRef = doc(db, "organizations", orgId, "miembros", uid);
  batch.set(memberRef, {
    uid,
    email: normalizedEmail,
    role: 'owner',
    joinedAt: serverTimestamp()
  } as OrgMember);

  const mirrorRef = doc(db, "usuarios", uid, "organizaciones", orgId);
  batch.set(mirrorRef, {
    orgId,
    role: 'owner',
    type: 'personal',
    joinedAt: serverTimestamp()
  } as UserOrgLink);

  await batch.commit();
  return orgId;
};

/**
 * Crea un equipo nuevo (org tipo "equipo"), con `uid` como owner. A
 * diferencia de la org personal, el id es auto-generado (un usuario puede
 * tener varios equipos) y la creación SIEMPRE crea un doc nuevo — no hay
 * chequeo de idempotencia porque cada llamada es una acción explícita del
 * usuario ("Crear equipo"), no un paso automático de login.
 *
 * A propósito son 2 escrituras SECUENCIALES (no un batch): la regla de
 * organizations/{orgId}/miembros/{uid} necesita poder hacer get() del doc
 * de organización para confirmar el ownerId, y eso solo es confiable si
 * el doc ya existe de antes — ver el comentario en firestore.rules. Si la
 * segunda escritura falla (red, cierre de pestaña) el equipo queda sin
 * doc de miembro — mismo tipo de estado parcial que ya sabe reparar el
 * script de backfill de Sub-fase C (aplica igual de bien a equipos que a
 * orgs personales, ya que ambos chequean org+miembro+mirror por separado).
 */
export const createTeamOrganization = async (
  uid: string,
  email: string,
  name: string
): Promise<string> => {
  if (!uid) throw new Error("UID no proporcionado.");
  const trimmedName = (name || '').trim();
  if (!trimmedName) throw new Error("El nombre del equipo es obligatorio.");

  // Precheck del límite de equipos por usuario (proyecto_limites_organizacion)
  // — el freno real vive en firestore.rules (allow create de
  // organizations/{orgId}, rama "Crear equipo"), esto es solo para un
  // mensaje legible en vez de "Missing or insufficient permissions".
  const userSnap = await getDoc(doc(db, "users", uid));
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const limite = userData.limiteEquipos ?? Infinity;
    const cantidad = userData.cantidadEquiposCreados ?? 0;
    if (cantidad >= limite) {
      throw new Error(`Llegaste al límite de ${limite} equipo(s) que podés crear. Contactá al administrador para ampliarlo.`);
    }
  }

  const orgRef = doc(collection(db, "organizations"));
  await setDoc(orgRef, {
    name: trimmedName,
    type: 'equipo',
    ownerId: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
    // cantidadMiembros: ídem comentario de ensurePersonalOrganization —
    // los límites viven en users/{uid}, no acá.
  });

  const normalizedEmail = (email || '').toLowerCase();
  const memberRef = doc(db, "organizations", orgRef.id, "miembros", uid);
  await setDoc(memberRef, {
    uid,
    email: normalizedEmail,
    role: 'owner',
    joinedAt: serverTimestamp()
  } as OrgMember);

  const mirrorRef = doc(db, "usuarios", uid, "organizaciones", orgRef.id);
  await setDoc(mirrorRef, {
    orgId: orgRef.id,
    role: 'owner',
    type: 'equipo',
    joinedAt: serverTimestamp()
  } as UserOrgLink);

  return orgRef.id;
};

/**
 * Lista las organizaciones de las que `uid` es miembro, vía el mirror
 * usuarios/{uid}/organizaciones (barato: 1 query + N getDoc en paralelo,
 * sin collection-group queries). Usado por useOrganizations.ts.
 */
export const getUserOrganizations = async (uid: string): Promise<Organization[]> => {
  if (!uid) return [];

  const mirrorSnap = await getDocs(collection(db, "usuarios", uid, "organizaciones"));
  if (mirrorSnap.empty) return [];

  // El mirror trae el `role` del usuario en cada org (Sub-fase G1) — el
  // doc organizations/{orgId} en sí NO lo tiene, es contextual a quién
  // pregunta, así que se completa acá con lo que ya trajo el mirror.
  const roleByOrgId = new Map(mirrorSnap.docs.map(d => [d.id, (d.data() as UserOrgLink).role]));
  const unidoEnByOrgId = new Map(mirrorSnap.docs.map(d => [d.id, (d.data() as UserOrgLink).joinedAt]));

  const orgDocs = await Promise.all(
    mirrorSnap.docs.map(linkDoc => getDoc(doc(db, "organizations", linkDoc.id)))
  );

  return orgDocs
    .filter(d => d.exists())
    .map(d => ({ id: d.id, ...d.data(), role: roleByOrgId.get(d.id), unidoEn: unidoEnByOrgId.get(d.id) } as Organization));
};

// Organización sin `role` (a diferencia de Organization, ver
// types/organization.ts) — ese campo es contextual a qué usuario pregunta
// ("mi rol en esta org"), no tiene sentido para un listado admin de TODAS
// las organizaciones sin importar de quién.
export type OrganizacionSinRol = Omit<Organization, "role">;

/**
 * TODAS las organizaciones de la plataforma, sin filtrar por usuario —
 * para la sección Soporte (ver SoporteHome.tsx). A diferencia de
 * getUserOrganizations (mirror del propio usuario), esto SÍ es un
 * `list()` directo de la colección completa — firestore.rules ya lo deja
 * exclusivo de isAdmin(), no hace falta ningún cambio de rules para esto.
 */
export const listAllOrganizationsAdmin = async (): Promise<OrganizacionSinRol[]> => {
  const snap = await getDocs(collection(db, "organizations"));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Organization, "id" | "role">) }));
};

// Los updateOrgLimite* (proyectos/miembros/miembrosPorProyecto) que vivían
// acá se eliminaron en el rediseño de 2026-08-19 — esos límites ya no son
// por-org, ver updateUserLimiteProyectos/updateUserLimiteMiembrosPorProyecto/
// updateUserLimiteMiembrosPorOrganizacion en userService.ts.

/**
 * Renombra un equipo — solo el OWNER puede (ver firestore.rules, mismo
 * criterio que "solo el creador archiva" en projects/{id}). No aplica a
 * la org personal (nombre auto-generado, no se expone UI para editarla).
 */
export const renameOrganization = async (orgId: string, nuevoNombre: string): Promise<void> => {
  const trimmed = nuevoNombre.trim();
  if (!trimmed) throw new Error("El nombre no puede estar vacío.");
  await updateDoc(doc(db, "organizations", orgId), { name: trimmed, updatedAt: serverTimestamp() });
};

/**
 * Archiva o reactiva un equipo (2026-08-20, a pedido del usuario) — "cerrar
 * sin perder nada": una org archivada no admite proyectos ni miembros
 * nuevos (ver firestore.rules, match /projects/{id} y /miembros/{uid}),
 * pero todo lo que ya tenía sigue accesible y se puede reactivar en
 * cualquier momento. Solo el OWNER puede tocar este campo.
 */
export const setOrganizationArchivada = async (orgId: string, archivada: boolean): Promise<void> => {
  await updateDoc(doc(db, "organizations", orgId), { archivada, updatedAt: serverTimestamp() });
};

/**
 * Fallback compartido (Sub-fase G1): si no hay una `orgActual` resuelta
 * (localStorage vacío, org borrada, aún cargando), cae a la org personal
 * del usuario — nunca deja crear un proyecto/cliente sin `orgId`.
 * Reutilizado por createProject y createCliente para no duplicar el
 * mismo criterio en cada componente que los llama.
 */
export const resolveOrgId = (orgActual: Organization | null | undefined, uid: string): string =>
  orgActual?.id || personalOrgId(uid);

// ─── Sub-fase M1+M2: gestión de miembros ────────────────────────────────

export interface OrgMemberConNombre extends OrgMember {
  id: string; // == uid, para no repetir la key al mapear en la UI
  displayName: string; // users/{uid}.name si existe, si no el email
}

/**
 * Lista los miembros de una org con nombre resuelto para mostrar — no se
 * denormaliza el nombre en el doc de miembros (evitaría quedar desactualizado
 * si el usuario edita su nombre después) sino que se lee `users/{uid}.name`
 * en paralelo, con fallback al email guardado en el propio doc de miembro
 * si el usuario no tiene nombre configurado. Ordenados por antigüedad
 * (owner primero, casi siempre).
 */
export const getOrgMembers = async (orgId: string): Promise<OrgMemberConNombre[]> => {
  const snap = await getDocs(collection(db, "organizations", orgId, "miembros"));
  const miembros = snap.docs.map(d => ({ id: d.id, ...(d.data() as OrgMember) }));

  const conNombre = await Promise.all(miembros.map(async (m) => {
    let displayName = m.email;
    try {
      const userSnap = await getDoc(doc(db, "users", m.id));
      const nombre = userSnap.exists() ? (userSnap.data().name as string | undefined) : undefined;
      if (nombre?.trim()) displayName = nombre.trim();
    } catch {
      // Si falla la lectura del perfil, se muestra el email igual — nunca
      // bloquea la lista completa por un solo miembro.
    }
    return { ...m, displayName };
  }));

  return conNombre.sort((a, b) => {
    if (a.role === 'owner') return -1;
    if (b.role === 'owner') return 1;
    return a.displayName.localeCompare(b.displayName);
  });
};

/**
 * M2, rama "ya existe": agrega a una cuenta de plataforma existente como
 * miembro de la org. El caller (GestionarMiembrosModal.tsx) ya hizo el
 * lookup con findPlatformUserByEmail — no se repite acá para no crear un
 * import circular organizationService.ts <-> userService.ts (userService.ts
 * ya importa ensurePersonalOrganization de este mismo archivo).
 */
export const addExistingUserToOrg = async (
  orgId: string,
  orgType: OrgType,
  uid: string,
  email: string,
  rol: OrgRoleAsignable
): Promise<void> => {
  // Precheck del límite de miembros (proyecto_limites_organizacion) — el
  // freno real vive en firestore.rules, esto es solo para mostrar un
  // mensaje legible en vez de "Missing or insufficient permissions".
  // Rediseño 2026-08-19: el LÍMITE ya no vive en la org sino en el dueño
  // de la org (users/{ownerId}.limiteMiembrosPorOrganizacion) — el
  // contador (cantidadMiembros) sigue siendo por-org, eso no cambió.
  const orgSnap = await getDoc(doc(db, "organizations", orgId));
  let orgNombre = 'tu organización';
  if (orgSnap.exists()) {
    const org = orgSnap.data();
    orgNombre = org.name || orgNombre;
    // Precheck de org archivada (2026-08-20) — mismo criterio, mensaje legible.
    if (org.archivada === true) {
      throw new Error("Esta organización está archivada — no admite miembros nuevos. Reactivala primero.");
    }
    const ownerSnap = await getDoc(doc(db, "users", org.ownerId));
    const limite = ownerSnap.exists() ? (ownerSnap.data().limiteMiembrosPorOrganizacion ?? Infinity) : Infinity;
    const cantidad = org.cantidadMiembros ?? 0;
    if (cantidad >= limite) {
      throw new Error(`Llegaste al límite de ${limite} miembro(s) para esta organización. Contactá al administrador para ampliarlo.`);
    }
  }

  const normalizedEmail = email.trim().toLowerCase();
  const batch = writeBatch(db);
  batch.set(doc(db, "organizations", orgId, "miembros", uid), {
    uid,
    email: normalizedEmail,
    role: rol,
    joinedAt: serverTimestamp()
  } as OrgMember);
  batch.set(doc(db, "usuarios", uid, "organizaciones", orgId), {
    orgId,
    role: rol,
    type: orgType,
    joinedAt: serverTimestamp()
  } as UserOrgLink);
  await batch.commit();

  // "Notificaciones más ricas" (2026-08-20, Fase 3) — hueco real detectado:
  // hasta acá un usuario agregado a una org no se enteraba de nada salvo
  // que iniciara sesión y viera "Equipo Yeison" nuevo en el switcher. No
  // corta el alta si el envío falla, mismo criterio que el resto de la app.
  queueEmail({
    to: [normalizedEmail],
    subject: `Te agregaron a ${orgNombre} en OneHub`,
    html: `
      <p>Te agregaron como <strong>${escapeHtml(ROL_LABEL[rol] || rol)}</strong> a <strong>${escapeHtml(orgNombre)}</strong> en OneHub.</p>
      <p>Inicia sesión con este mismo correo (${escapeHtml(normalizedEmail)}) y la verás en tu selector de organizaciones.</p>
      ${botonOneHub('Abrir OneHub')}
    `
  }).catch((err) => console.error('Error notificando alta de miembro por email:', err));
  notifySlackOrg(orgId, `👋 *${normalizedEmail}* se unió a *${orgNombre}* como ${rol}.`)
    .catch((err) => console.error('Error notificando alta de miembro a Slack:', err));
};

/**
 * M2, rama "no existe todavía": deja una invitación pendiente, que se
 * aplica sola cuando esa cuenta se registre (ver processOrgInvitations en
 * userService.ts). Idempotente por diseño: re-invitar al mismo email
 * simplemente sobreescribe el doc anterior (mismo id determinístico).
 */
export const createPendingInvitation = async (
  orgId: string,
  orgNombre: string,
  orgType: OrgType,
  email: string,
  rol: OrgRoleAsignable,
  invitadoPor: string
): Promise<void> => {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) throw new Error("El email es obligatorio.");

  await setDoc(doc(db, "organizations", orgId, "invitacionesPendientes", normalizedEmail), {
    orgId,
    orgNombre,
    orgType,
    email: normalizedEmail,
    rol,
    invitadoPor,
    invitadoEn: serverTimestamp()
  } as PendingInvitation);

  // El correo ya no sale de acá (2026-10-26): lo envía crearCuentaInvitadoOrg
  // (bienvenida con "Crear mi contraseña", como la invitación a proyecto) y,
  // si eso falla, avisarInvitacionPendiente.
  notifySlackOrg(orgId, `✉️ Se invitó a *${normalizedEmail}* a unirse a *${orgNombre}* como ${rol} (pendiente de que se registre).`)
    .catch((err) => console.error('Error notificando invitación a Slack:', err));
};

/**
 * Igual que la invitación a proyecto (2026-10-26): crea la cuenta de quien
 * todavía no tiene una y le envía el correo para crear su contraseña. Exige
 * que la invitación pendiente ya exista (createPendingInvitation), que es la
 * que lo agrega a la org en su primer ingreso. Ver functions/callable/createOrgInvitee.js.
 */
export const crearCuentaInvitadoOrg = async (orgId: string, email: string, nombreCompleto: string): Promise<{ uid: string; correoEnviado: boolean }> => {
  const callable = httpsCallable(functionsClient, 'createOrgInvitee');
  const result = await callable({ orgId, email, nombreCompleto, appUrl: window.location.origin });
  return result.data as { uid: string; correoEnviado: boolean };
};

/** Plan B si no se pudo crear la cuenta: aviso con enlace para registrarse con ese correo. */
export const avisarInvitacionPendiente = async (orgNombre: string, email: string, rol: OrgRoleAsignable) => {
  const normalizedEmail = email.trim().toLowerCase();
  await queueEmail({
    to: [normalizedEmail],
    subject: `Te invitaron a unirte a ${orgNombre} en OneHub`,
    html: `
      <p>Te invitaron a unirte a <strong>${escapeHtml(orgNombre)}</strong> en OneHub, como <strong>${escapeHtml(ROL_LABEL[rol] || rol)}</strong>.</p>
      <p>Regístrate con este mismo correo (${escapeHtml(normalizedEmail)}) y quedarás agregado automáticamente.</p>
      ${botonOneHub('Ir a OneHub')}
    `
  }).catch((err) => console.error('Error notificando invitación por email:', err));
};

export const getPendingInvitations = async (orgId: string): Promise<(PendingInvitation & { id: string })[]> => {
  const snap = await getDocs(collection(db, "organizations", orgId, "invitacionesPendientes"));
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as PendingInvitation) }));
};

/** Revocar una invitación aún no aceptada (owner/admin). */
export const revokeInvitation = async (orgId: string, email: string): Promise<void> => {
  await deleteDoc(doc(db, "organizations", orgId, "invitacionesPendientes", email.trim().toLowerCase()));
};

/**
 * Cambia el rol de un miembro existente. Actualiza AMBOS docs (fuente de
 * verdad + mirror) en un batch — a diferencia de la creación, quien llama
 * esto normalmente NO es el dueño del mirror (es un owner/admin actuando
 * sobre otro miembro), por eso la rule de usuarios/{uid}/organizaciones/{orgId}
 * tiene una rama explícita para esOwnerOAdminDeOrg (ver firestore.rules).
 */
export const changeOrgMemberRole = async (orgId: string, uid: string, rol: OrgRoleAsignable): Promise<void> => {
  const batch = writeBatch(db);
  batch.update(doc(db, "organizations", orgId, "miembros", uid), { role: rol });
  batch.update(doc(db, "usuarios", uid, "organizaciones", orgId), { role: rol });
  await batch.commit();
};

/**
 * Remueve a un miembro de la org (nunca al owner, ver firestore.rules).
 * Borra AMBOS docs. Los proyectos donde ese usuario tenga un rol explícito
 * (guest) NO se tocan — solo pierde el acceso implícito de miembro de org.
 */
export const removeOrgMember = async (orgId: string, uid: string): Promise<void> => {
  const batch = writeBatch(db);
  batch.delete(doc(db, "organizations", orgId, "miembros", uid));
  batch.delete(doc(db, "usuarios", uid, "organizaciones", orgId));
  await batch.commit();
};
